// Framework detection for `cloudgrid plug` auto-init (Option E — see
// docs/brainstorms/2026-06-04-cli-usecase-trichotomy.md §4 Option E and
// spec v17 §7.4).
//
// When `cloudgrid plug` runs in a directory that has no `.cloudgrid/link.json`
// AND no `cloudgrid.yaml`, the CLI sniffs the directory to decide what kind of
// service it's looking at. The result drives the generated `cloudgrid.yaml`
// (one `web` service of the detected type, sourced from the directory root).
//
// This module is pure logic — it reads the filesystem but never writes and
// never prints. The verb that calls it owns all user-facing I/O. Detection is
// deliberately conservative: when nothing matches, it returns null and the
// caller falls back to an explicit `cloudgrid init`.

import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

import type { ServiceType } from '../commands/scaffold/templates.js';

/** Service types auto-init can generate. Mirrors `ALLOWED_INIT_TYPES` in
 *  commands/init.ts — `cron` is excluded (it needs a schedule, never the
 *  first thing detected from a bare directory). */
export type DetectedType = Extract<ServiceType, 'node' | 'nextjs' | 'python' | 'static'>;

export interface DetectedBuild {
  /** Build command run server-side before bundling the static layer. */
  command: string;
  /** Directory the build writes its static output to (maps to `build.output`). */
  output: string;
}

export interface FrameworkDetection {
  /** The service `type:` to write into the generated yaml. */
  type: DetectedType;
  /** Human-readable framework label for the confirm prompt (§23 voice). */
  label: string;
  /** The file(s) that drove the decision — shown to the user so the guess
   *  is legible, not magic. */
  evidence: string[];
  /** Present for static-build frameworks (Vite / Astro / SvelteKit / Nuxt):
   *  the server-side build step the generated yaml should declare. Absent for
   *  plain static sites (pre-built HTML), node, nextjs, and python. */
  build?: DetectedBuild;
}

/** Human labels per detected type. Plain, no marketing — §23. */
const LABELS: Record<DetectedType, string> = {
  nextjs: 'Next.js app',
  node: 'Node.js app',
  python: 'Python app',
  static: 'static site',
};

/**
 * Static-build frameworks that ship a source tree compiled to static HTML.
 * Without this pass they all look like a plain `node` app (they have a
 * package.json) and fail validation, since they declare neither a `main` nor
 * a `scripts.start`. Each maps to `type: static` plus the build command and
 * output directory the framework writes to.
 *
 * Order matters: SvelteKit / Astro / Nuxt all depend on Vite transitively and
 * may list `vite` in their own package.json, so the more specific signals are
 * checked before the generic `vite` fallback (a SvelteKit project must resolve
 * to `output: build`, not `output: dist`).
 */
const STATIC_BUILD_FRAMEWORKS: ReadonlyArray<{
  dep: string;
  label: string;
  output: string;
}> = [
  { dep: '@sveltejs/kit', label: 'SvelteKit app', output: 'build' },
  { dep: 'astro', label: 'Astro site', output: 'dist' },
  { dep: 'nuxt', label: 'Nuxt app', output: '.output/public' },
  { dep: 'vite', label: 'Vite app', output: 'dist' },
];

/** Read + parse package.json at `dir`, or null when absent/unreadable. */
function readPackageJson(dir: string): Record<string, unknown> | null {
  const path = join(dir, 'package.json');
  if (!existsSync(path)) return null;
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8'));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** True when `dep` appears in dependencies or devDependencies. */
function hasDep(pkg: Record<string, unknown>, dep: string): boolean {
  for (const key of ['dependencies', 'devDependencies']) {
    const deps = pkg[key];
    if (deps && typeof deps === 'object' && dep in (deps as object)) {
      return true;
    }
  }
  return false;
}

/** True when package.json declares a non-empty `scripts.start` — the marker of
 *  a service with its own server entry point (vs a build-only static SPA). */
function hasStartScript(pkg: Record<string, unknown>): boolean {
  const scripts = pkg.scripts;
  if (!scripts || typeof scripts !== 'object') return false;
  const start = (scripts as Record<string, unknown>).start;
  return typeof start === 'string' && start.trim().length > 0;
}

/**
 * Detect the framework of an existing local project from its files.
 *
 * Order matters — the first rule that matches wins:
 *   1. `package.json` with a `next` dependency        → nextjs
 *   2. `package.json` with a `scripts.start`           → node (QA-03)
 *      (a server entry point — full-stack app that runs and serves its own
 *      build; wins over a static-build dep so React+Express / Vue+Fastify
 *      apps keep their backend)
 *   3. `package.json` with a static-build framework    → static (+ build)
 *      (Vite / Astro / SvelteKit / Nuxt, with NO server entry point — a pure
 *      pre-rendered SPA)
 *   4. `package.json` (any other)                     → node
 *   5. `requirements.txt`, `src/main.py`, or `main.py`→ python
 *   6. `index.html`                                   → static
 *   7. nothing recognised                             → null
 *
 * Returns null when the directory is ambiguous or empty; the caller then
 * points the user at an explicit `cloudgrid init`.
 */
export function detectFramework(dir: string): FrameworkDetection | null {
  const pkg = readPackageJson(dir);
  if (pkg) {
    if (hasDep(pkg, 'next')) {
      return { type: 'nextjs', label: LABELS.nextjs, evidence: ['package.json'] };
    }
    // QA-03: a project with a `scripts.start` ships a server entry point — it
    // runs and serves its own frontend build. It must resolve to `node` even
    // when a static-build framework (Vite / Astro / SvelteKit / Nuxt) appears
    // in its dependencies, because those frameworks are commonly the FRONTEND
    // build tooling sitting next to an Express/Fastify backend
    // (devDependencies: vite, dependencies: express, scripts.start: a server).
    // Classifying such an app as `static` drops the backend entirely and the
    // deploy ships as nginx-static. The static-build classification below is
    // therefore the fallback for projects that have a build but NO server
    // entry point. Do NOT reorder past the `next` check: a Next.js app also
    // has a `start` script but stays `type: nextjs`.
    if (hasStartScript(pkg)) {
      return { type: 'node', label: LABELS.node, evidence: ['package.json'] };
    }
    for (const fw of STATIC_BUILD_FRAMEWORKS) {
      if (hasDep(pkg, fw.dep)) {
        return {
          type: 'static',
          label: fw.label,
          evidence: ['package.json'],
          build: { command: 'npm run build', output: fw.output },
        };
      }
    }
    return { type: 'node', label: LABELS.node, evidence: ['package.json'] };
  }

  for (const rel of ['requirements.txt', 'src/main.py', 'main.py']) {
    if (existsSync(join(dir, rel))) {
      return { type: 'python', label: LABELS.python, evidence: [rel] };
    }
  }

  if (existsSync(join(dir, 'index.html'))) {
    return { type: 'static', label: LABELS.static, evidence: ['index.html'] };
  }

  return null;
}
